import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { Typography, Grid, Tooltip, Box, Stack, IconButton } from '@mui/material';
import { getRentalLogRedux } from '../../../redux/slices/rentals';
import { useSelector, useDispatch } from '../../../redux/store';
import Scrollbar from '../../../reusable-components/scrollbar';
import Iconify from '../../../reusable-components/iconify';
import LogSearchBar from '../../../reusable-components/log-search-bar/LogSearchBar';
import { useLogSearch, createSearchToggleHandler } from '../../../reusable-components/log-search-bar/logSearchUtils';
import LogSearchEntry from '../../../reusable-components/log-search-bar/LogSearchEntry';
import { enqueueSnackbar } from '../../../reusable-components/snackbar';

LogTemplate.propTypes = {
  icon: PropTypes.string,
  message: PropTypes.element,
  logEntry: PropTypes.object,
  idx: PropTypes.number,
};
function LogTemplate({ icon, message, logEntry, idx }) {
  let addedBy = logEntry?.user?.fullName;
  const hasColon = addedBy?.indexOf(':') > -1;
  if (hasColon) {
    addedBy = addedBy?.split(':', 1);
  }

  const setLineHeight = () => {
    const container = document.querySelector('.logEntries');
    const line = document.querySelector('.logLine');
    if (line !== null && container !== null) {
      line.style.height = `${container.scrollHeight}px`;
    }
  };

  const styleDate = (dateStr) => {
    if(!dateStr) return '';
    const date = new Date(dateStr);
    const formattedDate = date.toLocaleString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: 'numeric', hour12: true });
    return (
      <Tooltip title={formattedDate}>
        <Typography
          variant="subtitle2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'noWrap',
          }}
        >
          {formattedDate}
        </Typography>
      </Tooltip>
    );
  };
  
  window.addEventListener('load', setLineHeight);
  window.addEventListener('resize', setLineHeight);
  setTimeout(setLineHeight, 100);

  return (
    <>
      {logEntry === 1 ? (
        <Grid container direction="row" alignItems="top" >
          <Grid item xs={2}>
            <div
              style={{
                width: '5px',
                height: '1px',
                backgroundColor: '#208ee078',
                position: 'absolute',
                top: '28px',
                left: '15px',
              }}
            >
              {' '}
            </div>
            <Box
              sx={{
                position: 'absolute',
                top: '12px',
                left: '20px',
                backgroundColor: 'white',
                zIndex: 120,
                justifyContent: 'center',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                padding: '5px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Iconify icon={icon} sx={{ zIndex: 12 }} color="info" height="18px" width="18px" />
            </Box>
          </Grid>
          <Grid item xs={10}>
            <Box sx={{ fontSize: '14px', wordBreak: 'normal' }}>
                {message}
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box
          sx={{
            zIndex: 5,
            position: 'relative',
            maxWidth: '95%',
            overflowWrap: 'normal',
            marginTop: idx === 0 ? '15px' : '40px',
            maxHeight: '100%'
          }}
        >
          <div
            style={{
              width: '5px',
              height: '1px',
              backgroundColor: '#208ee078',
              position: 'absolute',
              top: '10px',
              left: '15px',
            }}
          >
            {' '}
          </div>
          <Box
            sx={{
              position: 'absolute',
              top: '-5px',
              left: '20px',
              backgroundColor: 'white',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
              padding: '5px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Iconify icon={icon} color="info" height="18px" width="18px" />
          </Box>
          <Box
            sx={{
              fontSize: '14px',
              wordBreak: 'normal',
              marginLeft: '35px',
              zIndex: '0',
              display: 'relative',
              paddingLeft: '25px',
            }}
          >
            {message}
             <Typography style = {{
                display: 'flex',
                justifyContent: 'left',
                paddingRight: '2px',
                color: '#212B36',
                marginTop: '5px',
                alignItems: 'top',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '14px'}}>
                {styleDate(logEntry?.dateAdded)}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
}

export const RentalLog = ({rentalId,  setOpenEmailDialog, setDialogContent, openTicket}) => {
  const { data: { rentalLog }} = useSelector((state) => state.rentals);
  const dispatch = useDispatch();
  const [expand, setExpand] = useState(true);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const parseJsonSafe = (value, fallback = null) => {
    if (typeof value !== 'string') {
      return fallback;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };

  useEffect(() => { 
    if(openTicket === 'open') {
      setExpand(false);
    } 
  }, [openTicket]);

  useEffect(() => { 
    dispatch(getRentalLogRedux(rentalId));
  }, []);

  const handleSearchToggle = createSearchToggleHandler(setShowSearchBar, setSearchQuery, () => setExpand(true));

  const handleToggleExpand = () => {
    setExpand((prevExpand) => {
      const next = !prevExpand;
      if (!next) {
        setShowSearchBar(false);
        setSearchQuery('');
      }
      return next;
    });
  };

  const { containerRef, isHidden, matchCount } = useLogSearch(searchQuery);

  // Text users see only in a tooltip (the email behind @name) or the discharge
  // email dialog, added as search-only text so it is still searchable.
  const getHiddenSearchText = (logEntry) => {
    const parts = [logEntry?.user?.email];
    if (logEntry?.messageType === 'ResidentDeleted') {
      const email = parseJsonSafe(logEntry?.extraInfo, null);
      const recipients = email?.vendorRec ? Object.values(email.vendorRec).flat() : [];
      const body = email?.body ? new DOMParser().parseFromString(email.body, 'text/html').body.textContent : '';
      parts.push(email?.subject, ...recipients, body);
    }
    return parts.filter((part) => typeof part === 'string' && part);
  };

  const titleStyle =  {fontSize: '14px', paddingRight: '2px', fontWeight: 600, overflowWrap: 'normal', wordBreak: 'normal', whiteSpace: 'normal'};
  const BasicTitle = ({message, user}) => (
      <Typography color="info.main" sx={titleStyle}>
        {message} by <CopyTooltip user={user} />
      </Typography>
    );
  const getHtmlMessage = (logEntry) => {
    const parsedExtraInfo = parseJsonSafe(logEntry?.extraInfo, null);
    const extraInfoArray = Array.isArray(parsedExtraInfo) ? parsedExtraInfo : [];
    switch (logEntry.messageType) {
      case 'View':
        return <BasicTitle message="Rental was viewed" user={logEntry?.user} />;
      case 'ItemAdded':
        return (
          <>
            <BasicTitle message="Item was added" user={logEntry?.user} />
            {Array.isArray(extraInfoArray) && extraInfoArray?.map((item, index) => (
              <li key={index} style={{ fontSize: '12px', color: 'text.secondary' }}>
                {item.Description} (Qty: {item.Quantity})
              </li>
            ))}
          </>
        );
      case 'ItemDeleted':
        return (
          <>
            <BasicTitle message="Item was deleted" user={logEntry?.user} />
            {Array.isArray(extraInfoArray) && extraInfoArray?.map((item, index) => (
              <li key={index} style={{ fontSize: '12px', color: 'text.secondary' }}>
                {item}
              </li>
            ))}
            <span style={{ fontSize: '12px', color: 'text.secondary' }}>deleted.</span>
          </>
        );
      case 'ItemQuantityChanged':
        return (
          <>
            <BasicTitle message="Item quantity was changed" user={logEntry?.user} />
            {Array.isArray(extraInfoArray) && extraInfoArray?.map((item, index) => (
              <li key={index} style={{ fontSize: '12px', color: 'text.secondary' }}>
                {item.Description} (Old Qty: {item.oldQty}) (New Qty: {item.newQty})
              </li>
            ))}
          </>
        );
      case 'NewRental':
        return <BasicTitle message="Rental was created" user={logEntry?.user} />;
      case 'OrderSplit':
        return (
          <>
            <BasicTitle message="Order was split" user={logEntry?.user} />
            <span style={{ display: 'inline' }}>
              {Array.isArray(parsedExtraInfo?.selectedItems) ? parsedExtraInfo.selectedItems.map((item, index) => (
                <li key={index} style={{ fontSize: '12px', color: 'text.secondary', marginRight: 4 }}>
                  {item.quantity ?? item.splitAmount} {item.description}
                </li>
              )) : ''}
              <span style={{ fontSize: '12px', color: 'text.secondary' }}>
                split from order #{parsedExtraInfo?.originalOrderId} to order #{parsedExtraInfo?.newOrderId}.
              </span>
            </span>
          </>
        );
      case 'Approve':
        return <BasicTitle message="Rental was approved" user={logEntry?.user} />;
      case 'CloseOut':
        return <BasicTitle message="Rental was closed out" user={logEntry?.user} />;
      case 'PickupRequested':
        return <BasicTitle message="Rental pickup requested" user={logEntry?.user} />;
      case 'VendorStatusUpdate':
        return (
          <Typography color="info.main" sx={titleStyle}>
            {parsedExtraInfo?.vendorName || 'Vendor'} updated {parsedExtraInfo?.description || 'an item'} from {parsedExtraInfo?.previousStatus || 'no previous status'} to {parsedExtraInfo?.status}.
          </Typography>
        );
      case 'ResidentDeleted':
        return (
          <>
            <Typography color="info.main" sx={titleStyle}>
              Resident was discharged from PCC
            </Typography>
            {parsedExtraInfo !== null && (
              <button
                type="button"
                style={{ cursor: "pointer", fontSize: '2px', color: 'text.secondary', border: 'none', background: 'none' }}
                onClick={() => {
                  setOpenEmailDialog(true);
                  setDialogContent(parsedExtraInfo);
                }}
              >
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', border: 'none', background: 'none' }}>
                  Show email
                </Typography>
              </button>
            )}
          </>
        );
      case 'ResidentUpdate':
        return (
          <>
            <Typography color="info.main" sx={titleStyle}>
              Resident information updated in PCC.
            </Typography>
            {Array.isArray(extraInfoArray) && extraInfoArray?.map((item, index) => (
              <li key={index} style={{ fontSize: '12px', color: 'text.secondary' }}>
                {item.Field} was updated from {item.OldValue !== null ? item.OldValue : "null"} to {item.NewValue !== null ? item.NewValue : "null"}
              </li>
            ))}
          </>
        );
      case 'AddCopiedUser':
        return (
          <Typography color="info.main" sx={titleStyle}>
            {Array.isArray(extraInfoArray) && extraInfoArray.map((item) => item.Email).join(', ')} added by <CopyTooltip user={logEntry?.user} />
          </Typography>
        );
      case 'DeleteCopiedUser':
        return (
          <Typography color="info.main" sx={titleStyle}>
            {extraInfoArray} deleted by <CopyTooltip user={logEntry?.user} />
          </Typography>
        );
      case 'ShortLongTerm':
        return (
          <>
            <BasicTitle message="Rental was updated" user={logEntry?.user} />
            <span style={{ fontSize: '12px', color: 'text.secondary' }}>
              {parsedExtraInfo?.Description} was updated to {parsedExtraInfo?.LongTerm === true ? 'long term' : 'short term'}
            </span>
          </>
        );
      case 'AddItemNote':
        return (
          <>
            <BasicTitle message="Rental was updated" user={logEntry?.user} />
            <span style={{ fontSize: '12px', color: 'text.secondary' }}>
              Note was added to {parsedExtraInfo?.description} - "{parsedExtraInfo?.note}"
            </span>
          </>
        );
      case 'AddNote':
        return (
          <>
            <BasicTitle message="Rental was updated" user={logEntry?.user} />
            <span style={{ fontSize: '12px', color: 'text.secondary' }}>
              Note was added: "{parsedExtraInfo?.message}"
            </span>
          </>
        );
      case 'CloseoutEmailScheduled':
        if (parsedExtraInfo?.status === 'Sent') {
          return <BasicTitle message="Rental closeout confirmation email sent" user={logEntry?.user} />;
        }

        if (parsedExtraInfo?.status === 'Cancelled') {
          return <BasicTitle message="Rental closeout confirmation cancelled" user={logEntry?.user} />;
        }

        if (parsedExtraInfo?.status === 'Failed') {
          return <BasicTitle message="Rental closeout confirmation email failed to send" user={logEntry?.user} />;
        }
    
        return (
          <>
            <BasicTitle message="Rental closeout confirmation scheduled" user={logEntry?.user} />
            <span style={{ fontSize: '12px', color: 'text.secondary' }}>
              Confirmation email will be sent to the facility on {parsedExtraInfo?.emailSending} to verify that the rental item was picked up.
            </span>
          </>
        );
      case 'VendorAcknowledgement':
        return (
          <Typography color="info.main" sx={titleStyle}>
            Vendor acknowledgement received: {parsedExtraInfo?.eventType || 'status update'}.
          </Typography>
        );
      default:
        return '';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Scrollbar sx={{ minHeight: 0, flex: 1, maxHeight: '100%' }}>
        <Box className="logContainer" sx={{ position: 'relative', overflowX: 'hidden', overflowY: 'hidden' }}>
          <Grid item>
            <Stack direction="row" onClick={handleToggleExpand} sx = {{cursor: 'pointer'}} spacing={1} alignItems="center" justifyContent="space-between" >
            <Typography
              alignItems="left"
              justifyContent="left"
              variant="subtitle1"
              sx={{ color: 'info.main', fontWeight: 'bold', fontSize: '15px'}}
            >
              Rental Log
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                aria-label="Search log"
                onClick={handleSearchToggle}
                sx={{ color: 'info.main', padding: 0, height: 'fit-content' }}
              >
                <Iconify icon="mdi:magnify" width={20} height={20} />
              </IconButton>
              <Tooltip title={`${!expand ? 'expand' : 'collapse'}`}>
                  <IconButton
                    sx={{ color: 'info.main', padding: 0, height: 'fit-content', mt: 0.5 }}
                  >
                    <Iconify icon={`ic:twotone-expand-${!expand ? 'more' : 'less'}`} width={20} height={20} />
                  </IconButton>
                </Tooltip>
            </Stack>
            </Stack>
          </Grid>

          {showSearchBar && (
            <Box sx={{ py: 1 }} onClick={(e) => e.stopPropagation()}>
              <LogSearchBar
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClose={handleSearchToggle}
              />
            </Box>
          )}

        {expand && searchQuery.trim() && matchCount === 0 && (
          <Typography variant="body2" color="text.secondary">
            No matching log items
          </Typography>
        )}

        { expand && (
          <Box ref={containerRef} className="logEntries" sx={{ position: 'relative' }}>
          <div
            className="logLine"
            style={{
              position: 'absolute',
              zIndex: 0,
              left: '0px',
              transform: 'translateX(-5px)',
              width: '1px',
              backgroundColor: '#208ee078',
              marginLeft: '20px',
              top: '2px'
            }}
          />
          {(() => {
            // idx drives the first entry's top margin, so count only entries left visible.
            let visibleIdx = -1;
            return (rentalLog || []).map((logEntry) => {
            const logObject =
              logEntry.messageType === 'View'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'hugeicons:view', logEntry }
                : logEntry.messageType === 'ItemAdded'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'gg:add', logEntry }
                : logEntry.messageType === 'ItemDeleted'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'flowbite:edit-outline', logEntry }
                : logEntry.messageType === 'ItemQuantityChanged'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'flowbite:edit-outline', logEntry }
                : logEntry.messageType === 'NewRental'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'system-uicons:create', logEntry }
                : logEntry.messageType === 'Submit'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'tabler:send', logEntry }
                : logEntry.messageType === 'Approve'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:approve', logEntry }
                : logEntry.messageType === 'CloseOut'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'iconamoon:close-bold', logEntry }
                 : logEntry.messageType === 'PickupRequested'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'akar-icons:send', logEntry }
                : logEntry.messageType === 'VendorStatusUpdate'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'pepicons-pop:refresh', logEntry }
                : logEntry.messageType === 'ResidentDeleted'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:house-export-outline', logEntry }
                : logEntry.messageType === 'ResidentUpdate'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'pepicons-pop:refresh', logEntry }
                : logEntry.messageType === 'AddCopiedUser'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mingcute:user-add-fill', logEntry }
                : logEntry.messageType === 'DeleteCopiedUser'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mingcute:user-remove-fill', logEntry }
                : logEntry.messageType === 'OrderSplit'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:call-split', logEntry }
                : logEntry.messageType === 'ShortLongTerm'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'flowbite:edit-outline', logEntry }
                : logEntry.messageType === 'AddItemNote'      
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'flowbite:edit-outline', logEntry }
                : logEntry.messageType === 'AddNote'  
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'flowbite:edit-outline', logEntry }
                : logEntry.messageType === 'CloseoutEmailScheduled'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:email-send-outline', logEntry }
                : logEntry.messageType === 'VendorAcknowledgement'
                ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:check-decagram-outline', logEntry }
                : '';
           
            if (logObject === '') return '';
            const hidden = isHidden(logEntry.id);
            if (!hidden) visibleIdx += 1;
            return (
              <LogSearchEntry
                key={logEntry.id}
                entryKey={logEntry.id}
                hidden={hidden}
                date={logEntry.dateAdded}
                searchText={getHiddenSearchText(logEntry)}
              >
                <LogTemplate
                  icon={logObject?.icon}
                  message={logObject?.htmlMessage}
                  logEntry={logEntry}
                  collapsedSize={logObject?.collapsedSize}
                  idx={Math.max(visibleIdx, 0)}
                />
              </LogSearchEntry>
            );
            });
          })()}
          </Box>
        )}
        </Box>
      </Scrollbar>
    </Box>
  );
};

const CopyTooltip = (user) => {
  const handleCopyToClipboard = (data) => {
    navigator.clipboard
      .writeText(data)
      .then(() => {
        enqueueSnackbar('Email copied to clipboard!', {
          variant: 'info',
        });
      })
      .catch((err) => {
        console.error('Failed to copy email: ', err);
      });
  };
  return (
    <Tooltip
      onClick={() => handleCopyToClipboard(user?.user?.email)}
      title={
        <Stack direction={'row'}>
          <Icon icon="ph:copy-bold" width="1rem" height="1rem" /> &nbsp;{user?.user?.email}
        </Stack>
      }
      arrow
    >
      <span>@{user?.user?.fullName}</span>
    </Tooltip>
  );
};